/**
 * L'export CSV d'ING, lu dans le MÊME journal que les CODA.
 *
 * ## Pourquoi il existe
 *
 * Deux constats, mesurés le 18 septembre 2026 sur le compte `BE64 3631 1005 7452` :
 *
 * 1. **Des relevés manquent.** Les relevés 1 à 95 et 103 à 150 de 2025 n'ont
 *    jamais été importés — janvier à début juin 2025. Demandés à ING, pas
 *    encore reçus. On ne peut pas faire dépendre la reprise d'une réponse.
 * 2. **Les sorties et les frais n'ont jamais été gardés.** Avant le 14 septembre
 *    l'import jetait les débits, et le lecteur CODA ne retient que les familles
 *    d'opération `01` et `02` : frais bancaires et opérations par carte
 *    n'entrent nulle part. Pour une comptabilité, chacun est une écriture.
 *
 * L'export CSV, lui, porte **tout** : 21 194 mouvements sur 2025 et 2026,
 * entrées, sorties, frais et cartes.
 *
 * ## Ce que ce module rend, et pourquoi c'est un `CodaMovement`
 *
 * Exactement la même forme que `parseCoda`. Ce n'est pas une coquetterie : tout
 * ce qui suit — le rapprochement, les échelons de confiance, la file manuelle,
 * le classement hors périmètre, l'écriture en base — est déjà écrit et éprouvé
 * pour cette forme. Un second moteur serait un second endroit où se tromper.
 *
 * ## La référence, et l'idempotence
 *
 * Le CSV porte un « Numéro de mouvement », mais c'est un **rang dans l'export**,
 * pas un identifiant de la banque : réexporter une autre période le change.
 * Il ne peut donc pas servir de clé.
 *
 * La référence est dérivée de ce qui, lui, ne change pas — date valeur, montant
 * signé, compte de contrepartie — plus le rang de l'opération parmi ses
 * jumelles du même jour. Deux virements identiques le même jour restent deux
 * mouvements : c'est le rang qui les distingue, jamais une fusion.
 *
 * ## Ce que ce module ne fait PAS
 *
 * Il ne décide pas si un mouvement est déjà en base. Un CODA et un CSV décrivent
 * le même virement avec des identifiants différents : les reconnaître l'un dans
 * l'autre demande de compter les mouvements déjà connus du jour, ce que seule la
 * base sait faire. Voir `clesDeMouvement` et la fonction SQL
 * `mouvements_bancaires_connus`.
 */

import {
  parseCommunication,
  type ParsedCommunication,
} from './communication';
import type { CodaMovement, MovementDirection } from './coda';

/** Les colonnes de l'export ING, dans l'ordre où la banque les écrit. */
export const COLONNES_ING = [
  'Numéro de compte',
  'Nom du compte',
  'Compte contrepartie',
  'Numéro de mouvement',
  'Date comptable',
  'Date valeur',
  'Montant',
  'Devise',
  'Libellés',
  'Détails du mouvement',
  'Message',
] as const;

export interface CsvStatement {
  accountIban: string;
  accountName: string;
  /** La période réellement couverte, lue sur les mouvements. */
  periodFrom: string;
  periodTo: string;
  movements: CodaMovement[];
  /** Ce que le fichier contenait et qu'on n'a pas pu lire. */
  problems: string[];
}

/* -------------------------------------------------------------------------- */
/* La lecture brute                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Décode les octets du fichier.
 *
 * L'export d'ING est en UTF-8 **avec** marque d'ordre des octets. Sans la
 * retirer, la première en-tête s'appellerait `\uFEFFNuméro de compte` et aucune
 * colonne ne serait trouvée.
 */
export function decodeCsvBytes(bytes: Uint8Array): string {
  const texte = new TextDecoder('utf-8').decode(bytes);
  return texte.charCodeAt(0) === 0xfeff ? texte.slice(1) : texte;
}

/**
 * Découpe une ligne de CSV en champs.
 *
 * Le séparateur est le point-virgule. Les guillemets protègent un champ et se
 * doublent à l'intérieur — c'est la convention que suit l'export, et les
 * libellés bancaires contiennent des points-virgules.
 */
export function decouperLigne(ligne: string): string[] {
  const champs: string[] = [];
  let courant = '';
  let entreGuillemets = false;

  for (let i = 0; i < ligne.length; i++) {
    const c = ligne[i];
    if (entreGuillemets) {
      if (c === '"') {
        if (ligne[i + 1] === '"') { courant += '"'; i++; } else { entreGuillemets = false; }
      } else {
        courant += c;
      }
    } else if (c === '"') {
      entreGuillemets = true;
    } else if (c === ';') {
      champs.push(courant); courant = '';
    } else {
      courant += c;
    }
  }
  champs.push(courant);
  return champs;
}

/** `1.234,56` ou `-37,30` en centimes signés. */
export function montantEnCentimes(champ: string): number {
  const nettoye = champ.trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const valeur = Number(nettoye);
  if (!Number.isFinite(valeur)) {
    throw new Error(`montant illisible : ${JSON.stringify(champ)}`);
  }
  return Math.round(valeur * 100);
}

/** `JJ/MM/AAAA` vers ISO `AAAA-MM-JJ`. Rend `null` sur un champ vide. */
export function dateIso(champ: string): string | null {
  const t = champ.trim();
  const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/* -------------------------------------------------------------------------- */
/* Ce que le libellé porte                                                     */
/* -------------------------------------------------------------------------- */

/**
 * La communication, extraite du libellé.
 *
 * ING écrit `Communication: <texte>` puis, éventuellement,
 * `Info personnelle: <identifiant technique>`. Cet identifiant est un uuid sans
 * tirets : le laisser entrer dans la communication ferait lire 32 caractères
 * hexadécimaux comme une suite de chiffres, et `parseCommunication` cherche
 * justement des suites de chiffres. On coupe donc avant.
 */
export function communicationDuLibelle(libelle: string): string {
  const i = libelle.indexOf('Communication');
  if (i < 0) return '';
  const apres = libelle.slice(i).replace(/^Communication\s*:?\s*/, '');
  const fin = apres.search(/\bInfo personnelle\s*:/);
  return (fin >= 0 ? apres.slice(0, fin) : apres).trim();
}

/**
 * Le nom de la contrepartie : ce qui suit `De:` ou `Vers:`, avant l'IBAN.
 *
 * ING écrit `De: Diallo Boubacar - BE95063777146958`. Le tiret est le seul
 * séparateur, et des noms en contiennent (`BALHOR - PAGEL`) : on coupe donc sur
 * le dernier tiret suivi de quelque chose qui ressemble à un compte, pas sur le
 * premier venu.
 */
export function contrepartieDuLibelle(libelle: string): string {
  const m = libelle.match(/\b(?:De|Vers)\s*:\s*(.+?)\s*-\s*[A-Z]{2}\d{2}[A-Z0-9]{8,}/);
  if (m) return m[1].trim();
  const sansIban = libelle.match(/\b(?:De|Vers)\s*:\s*([^:]+?)(?:\s+(?:Instantané|Communication|Info personnelle)\b|$)/);
  return sansIban ? sansIban[1].trim() : '';
}

/**
 * La famille d'opération, déduite du libellé.
 *
 * Le CSV ne porte pas le code opération CODA. On le reconstitue pour que le
 * reste du circuit raisonne comme sur un CODA : `01` virement SEPA, `02`
 * virement instantané, `04` opération par carte, `35` frais de la banque.
 * Seuls `01` et `02` sont des virements de compte à compte — donc du
 * rapprochement ; le reste est conservé sans jamais être rapproché.
 */
export function familleDuLibelle(libelle: string): string {
  const l = libelle.toLowerCase();
  if (l.startsWith('virement instantané')) return '02';
  if (l.startsWith('virement')) return '01';
  if (l.startsWith('domiciliation') || l.startsWith('ordre permanent')) return '01';
  if (l.startsWith('paiement') || l.startsWith('retrait')) return '04';
  if (l.startsWith('décompte de frais') || l.startsWith('intérêts')) return '35';
  return '';
}

/* -------------------------------------------------------------------------- */
/* La référence dérivée                                                        */
/* -------------------------------------------------------------------------- */

/**
 * La clé d'un mouvement bancaire, indépendante de la source qui le décrit.
 *
 * Date valeur, sens, montant, compte de contrepartie. C'est ce qu'un CODA et un
 * CSV disent tous les deux du même virement, et c'est donc là-dessus que se
 * reconnaît un mouvement déjà connu.
 *
 * La communication n'en fait **pas** partie : les deux formats l'écrivent
 * différemment — `+++511/5108/37402+++` d'un côté, `***511/5108/37402***` de
 * l'autre — et une clé qui diffère par la mise en forme ferait ré-entrer des
 * mouvements déjà là.
 */
export function cleDeMouvement(m: {
  valueDate: string;
  direction: MovementDirection;
  amountCents: number;
  counterpartyIban?: string;
}): string {
  const iban = (m.counterpartyIban ?? '').replace(/\s/g, '').toUpperCase();
  return `${m.valueDate}|${m.direction === 'debit' ? 'd' : 'c'}|${m.amountCents}|${iban}`;
}

/**
 * La référence d'un mouvement lu dans un CSV : sa clé, plus son rang parmi ses
 * jumelles.
 *
 * Le rang est ce qui permet à deux virements réellement identiques du même jour
 * de rester deux lignes, et à un même export rejoué de produire exactement les
 * mêmes références.
 */
export function referenceCsv(cle: string, rang: number): string {
  return `csv:${cle}#${rang}`;
}

/* -------------------------------------------------------------------------- */
/* Le fichier entier                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Lit un export CSV d'ING.
 *
 * Ce qui est écarté est **compté et nommé** dans `problems`, jamais avalé en
 * silence : les deux lignes « You have received a message » de l'export 2025,
 * par exemple, portent le numéro de mouvement `0`, un montant nul et aucune
 * date valeur. Ce ne sont pas des mouvements.
 */
export function parseCsvIng(entree: string | Uint8Array): CsvStatement {
  const texte = typeof entree === 'string'
    ? (entree.charCodeAt(0) === 0xfeff ? entree.slice(1) : entree)
    : decodeCsvBytes(entree);

  const lignes = texte.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lignes.length === 0) throw new Error('fichier vide');

  const entetes = decouperLigne(lignes[0]).map((c) => c.trim());
  const index = (nom: string) => {
    const i = entetes.indexOf(nom);
    if (i < 0) throw new Error(`colonne absente : ${nom}`);
    return i;
  };
  const iCompte = index('Numéro de compte');
  const iNom = index('Nom du compte');
  const iContrepartie = index('Compte contrepartie');
  const iSequence = index('Numéro de mouvement');
  const iDateValeur = index('Date valeur');
  /* Facultative : un export sans elle se lit encore, sans date comptable. */
  const iDateComptable = entetes.indexOf('Date comptable');
  const iMontant = index('Montant');
  const iDevise = index('Devise');
  const iLibelle = index('Libellés');
  const iDetail = index('Détails du mouvement');

  const problems: string[] = [];
  const bruts: (CodaMovement & { cle: string })[] = [];
  let accountIban = '';
  let accountName = '';
  let ignorees = 0;
  let devisesEtrangeres = 0;

  for (let n = 1; n < lignes.length; n++) {
    const champs = decouperLigne(lignes[n]);
    if (champs.length < entetes.length) {
      problems.push(`ligne ${n + 1} : ${champs.length} champs au lieu de ${entetes.length}`);
      continue;
    }

    const valueDate = dateIso(champs[iDateValeur]);
    const montant = montantEnCentimes(champs[iMontant]);
    if (valueDate === null || montant === 0) { ignorees++; continue; }

    const devise = champs[iDevise].trim();
    if (devise && devise !== 'EUR') { devisesEtrangeres++; continue; }

    accountIban ||= champs[iCompte].trim();
    accountName ||= champs[iNom].trim();

    const libelle = champs[iLibelle].trim();
    const detail = champs[iDetail].trim();
    const famille = familleDuLibelle(libelle);
    const iban = champs[iContrepartie].trim().replace(/\s/g, '').toUpperCase();
    const brutCommunication = communicationDuLibelle(libelle);
    const communication: ParsedCommunication = parseCommunication(brutCommunication);
    const direction: MovementDirection = montant < 0 ? 'debit' : 'credit';

    const mouvement: CodaMovement & { cle: string } = {
      sequence: champs[iSequence].trim(),
      reference: '',
      direction,
      amountCents: Math.abs(montant),
      valueDate,
      entryDate: iDateComptable >= 0 ? dateIso(champs[iDateComptable]) ?? undefined : undefined,
      transactionCode: '',
      family: famille,
      /* Un virement de compte à compte, et lui seul, entre au rapprochement.
         Sans IBAN de contrepartie il n'y a personne à rapprocher. */
      isTransfer: (famille === '01' || famille === '02') && iban.length > 0,
      communication,
      freeText: detail || libelle,
      counterpartyIban: iban || undefined,
      counterpartyName: contrepartieDuLibelle(libelle) || undefined,
      rawCommunicationField: brutCommunication || undefined,
      cle: '',
    };
    mouvement.cle = cleDeMouvement(mouvement);
    bruts.push(mouvement);
  }

  if (ignorees > 0) problems.push(`${ignorees} ligne(s) sans date valeur ou de montant nul, ecartees`);
  if (devisesEtrangeres > 0) problems.push(`${devisesEtrangeres} ligne(s) dans une autre devise, ecartees`);
  if (bruts.length === 0) throw new Error('aucun mouvement lisible dans ce fichier');

  /* Le rang parmi les jumelles, dans l'ordre du fichier. */
  const rangs = new Map<string, number>();
  for (const m of bruts) {
    const rang = (rangs.get(m.cle) ?? 0) + 1;
    rangs.set(m.cle, rang);
    m.reference = referenceCsv(m.cle, rang);
  }

  const dates = bruts.map((m) => m.valueDate).sort();
  return {
    accountIban,
    accountName,
    periodFrom: dates[0],
    periodTo: dates[dates.length - 1],
    /* `cle` ne sert qu'au calcul des rangs : elle ne quitte pas ce module. */
    movements: bruts.map((m) => { const copie = { ...m } as Partial<typeof m>; delete copie.cle; return copie as CodaMovement; }),
    problems,
  };
}

/** Les mouvements par jour, dans l'ordre. Le découpage d'envoi se fait par jour. */
export function parJourDeValeur(mouvements: CodaMovement[]): Map<string, CodaMovement[]> {
  const par = new Map<string, CodaMovement[]>();
  for (const m of mouvements) {
    const liste = par.get(m.valueDate);
    if (liste) liste.push(m); else par.set(m.valueDate, [m]);
  }
  return par;
}

/**
 * Ce qui reste à écrire, une fois retirés les mouvements que la base connaît déjà.
 *
 * `connus` compte, pour chaque forme, combien de mouvements la base porte —
 * qu'ils viennent d'un CODA, d'un CSV précédent ou d'un import à venir. On
 * écarte les `n` premiers de chaque forme et on garde le reste.
 *
 * Deux propriétés en découlent, et ce sont les deux qui comptent :
 *
 * - **rejouer le même fichier n'écrit rien** : ses lignes sont désormais comptées ;
 * - **le CODA qui arrive plus tard ne double rien** : il est compté à son tour,
 *   et le CSV correspondant ne repasse pas.
 *
 * Ce qui reste est conservé tel quel : un mouvement en trop côté base ne
 * supprime jamais rien — on n'ajoute pas, c'est tout.
 *
 * ## La seconde passe, et ce qu'elle a coûté d'apprendre
 *
 * La forme comprend le compte de contrepartie. Quand la base ne le connaît
 * pas, sa forme diffère de celle du CSV — qui, lui, le connaît toujours — et
 * le mouvement n'est pas reconnu.
 *
 * Ce n'est pas une hypothèse. Le 18 septembre 2026, l'import de l'export 2025
 * a **écrit 11 sorties en double**, 651,20 € : les seules sorties que la base
 * portait pour 2025 avaient été lues avant que l'import ne conserve l'IBAN du
 * bénéficiaire — 45 des 46 sorties historiques n'en ont aucun. Les entrées,
 * elles, n'ont pas bougé d'un centime : **100 % des 13 613 paiements bancaires
 * portent leur IBAN**, et 462 des 465 jours correspondaient exactement.
 *
 * D'où cette seconde passe : un mouvement du fichier qui connaît sa
 * contrepartie peut reconnaître, en base, un mouvement du **même jour, même
 * sens, même montant, dont la contrepartie est inconnue**. La base en sait
 * moins, pas autre chose.
 *
 * L'ordre des deux passes n'est pas indifférent : la forme complète d'abord,
 * de sorte qu'un mouvement parfaitement identifié ne consomme jamais la place
 * d'un autre, moins identifié, qui en aurait eu besoin.
 */
export function aEcrire(
  mouvements: CodaMovement[],
  connus: Record<string, number>,
): { garder: CodaMovement[]; deja: number } {
  const restant = new Map<string, number>();
  for (const [cle, n] of Object.entries(connus)) restant.set(cle, n);

  const reconnu = new Array<boolean>(mouvements.length).fill(false);
  let deja = 0;

  const consommer = (cle: string): boolean => {
    const n = restant.get(cle) ?? 0;
    if (n <= 0) return false;
    restant.set(cle, n - 1);
    deja++;
    return true;
  };

  // Passe 1 — la forme complète.
  mouvements.forEach((m, i) => {
    if (consommer(cleDeMouvement(m))) reconnu[i] = true;
  });

  // Passe 2 — la base ne connaît pas toujours la contrepartie.
  mouvements.forEach((m, i) => {
    if (reconnu[i] || !m.counterpartyIban) return;
    if (consommer(cleDeMouvement({ ...m, counterpartyIban: undefined }))) reconnu[i] = true;
  });

  /*
    Passe 3 — la contrepartie que la base connaît est parfois TRONQUÉE.

    Jusqu'au 18 septembre 2026, le lecteur CODA coupait le compte de la
    contrepartie à 16 caractères, la longueur d'un compte belge. Les comptes
    étrangers y perdaient leur fin : 81 paiements de onze pays, et **86 entrées
    écrites en double, 6 809,40 €**, parce que `PT50001000005988` et
    `PT50001000005988185000166` ne sont pas la même forme.

    Le lecteur est corrigé, mais les 13 613 paiements déjà écrits gardent leur
    compte amputé : rien ne permet de le reconstituer, sinon un export qui le
    porte en entier. Cette passe reconnaît donc un mouvement dont la base ne
    connaît qu'un PRÉFIXE du compte — et seulement un préfixe assez long pour
    être un compte (16 caractères), jamais un fragment quelconque.
  */
  const formesTronquees = [...restant.entries()]
    .filter(([cle, n]) => n > 0 && (cle.split('|')[3] ?? '').length >= 16);

  mouvements.forEach((m, i) => {
    if (reconnu[i] || !m.counterpartyIban) return;
    const complet = cleDeMouvement(m);
    const prefixe = formesTronquees.find(([cle]) =>
      (restant.get(cle) ?? 0) > 0 && cle !== complet && complet.startsWith(cle));
    if (prefixe && consommer(prefixe[0])) reconnu[i] = true;
  });

  return { garder: mouvements.filter((_m, i) => !reconnu[i]), deja };
}
